import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Loader2, Activity, Dumbbell, TrendingUp, Heart, Clock, StickyNote, Target, User, Download, MessageCircle, Compass, Flame, Ruler, Calendar, CheckCircle2, Circle, BookOpen, Zap, AlertTriangle, CreditCard, Bot, Mail, MailOpen, MousePointerClick, CheckCheck, XCircle, PercentCircle, Wrench, ClipboardList, Copy, Check, ChevronDown, ChevronUp, ChevronRight, Star, Watch, Trash2, X, Pin, Search, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/customSupabaseClient';
import { SendEmailButton } from '@/components/email/SendEmailButton';
import { toast } from 'sonner';
import NoteCard from '@/components/NoteReplyCard';
import DeleteUserDialog from '@/components/DeleteUserDialog';
import { AthleteTrainingCalendar } from '@/components/athlete-calendar/AthleteTrainingCalendar';
import SupportAssignmentControl from '@/components/SupportAssignmentControl';
import BookmarkedExercisesCard from '@/components/athlete/BookmarkedExercisesCard';
import { useAuth } from '@/contexts/SupabaseAuthContext';

const getEngagementStatus = (lastSignInAt) => {
  if (!lastSignInAt) return { label: 'Never Logged In', variant: 'secondary' };
  
  const daysSinceLogin = Math.floor((Date.now() - new Date(lastSignInAt)) / (1000 * 60 * 60 * 24));
  
  if (daysSinceLogin < 1) return { label: 'Active', variant: 'success' };
  if (daysSinceLogin <= 7) return { label: 'Inactive', variant: 'warning' };
  if (daysSinceLogin <= 30) return { label: 'At Risk', variant: 'warning' };
  return { label: 'Dormant', variant: 'destructive' };
};

const ProfileSkeleton = () => (
  <div className="space-y-8">
    <div className="flex flex-col sm:flex-row items-start gap-6">
      <Skeleton className="h-24 w-24 rounded-full" />
      <div className="flex-1 space-y-3">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-64" />
        <div className="flex gap-2 mt-2">
          <Skeleton className="h-5 w-16 rounded-md" />
          <Skeleton className="h-5 w-20 rounded-md" />
        </div>
      </div>
    </div>
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {[...Array(4)].map((_, i) => (
        <Card key={i}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-4" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-7 w-12" />
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

const SummarySkeleton = () => (
  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
    {[...Array(4)].map((_, i) => (
      <Card key={i}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-4" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-7 w-12" />
        </CardContent>
      </Card>
    ))}
  </div>
);

const ListSkeleton = () => (
  <div className="space-y-4">
    {[...Array(4)].map((_, i) => (
      <Card key={i} className="p-4">
        <div className="flex justify-between items-start">
          <div className="space-y-2 flex-1">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-5 w-20 rounded-md" />
        </div>
        <div className="mt-4 flex gap-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
        </div>
      </Card>
    ))}
  </div>
);

const formatRelativeTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffSeconds = Math.round((now - date) / 1000);
  const diffMinutes = Math.round(diffSeconds / 60);
  const diffHours = Math.round(diffMinutes / 60);
  const diffDays = Math.round(diffHours / 24);

  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const UserActivityDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canSeeBilling = can('users.billing');
  const canManageUser = can('users.manage');
  const canSeeAiChats = can('users.aiChats');
  const canSeeAlerts = can('users.alerts');
  const [user, setUser] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [painHistory, setPainHistory] = useState([]);
  const [notesHistory, setNotesHistory] = useState([]);
  const [dateRange, setDateRange] = useState('30');
  const [isExporting, setIsExporting] = useState(false);
  const [isFavvityUser, setIsFavvityUser] = useState(false);
  const [garminConnection, setGarminConnection] = useState(null);
  
  const [onboardingData, setOnboardingData] = useState(null);
  const [loadingOnboarding, setLoadingOnboarding] = useState(true);

  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [coachEngagement, setCoachEngagement] = useState(null);
  const [loadingCoachEngagement, setLoadingCoachEngagement] = useState(true);
  const [programProgress, setProgramProgress] = useState([]);
  const [loadingProgramProgress, setLoadingProgramProgress] = useState(true);
  const [emailHistory, setEmailHistory] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [aiConversations, setAiConversations] = useState([]);
  const [assignedSessions, setAssignedSessions] = useState([]);
  const [loadingAssignedSessions, setLoadingAssignedSessions] = useState(true);
  const [expandedAssignedSession, setExpandedAssignedSession] = useState(null);
  const [assignedSessionExercises, setAssignedSessionExercises] = useState({});
  const [loadingAssignedExercises, setLoadingAssignedExercises] = useState({});
  const [coaches, setCoaches] = useState([]);
  const [savingAssignedCoach, setSavingAssignedCoach] = useState(false);
  const [posthogActivity, setPosthogActivity] = useState(null);
  const [loadingPosthogActivity, setLoadingPosthogActivity] = useState(true);

  const [pinnedSessions, setPinnedSessions] = useState([]);
  const [loadingPinnedSessions, setLoadingPinnedSessions] = useState(true);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinSearchTerm, setPinSearchTerm] = useState('');
  const [pinSearchResults, setPinSearchResults] = useState([]);
  const [pinSearchLoading, setPinSearchLoading] = useState(false);
  const [pinningSessionId, setPinningSessionId] = useState(null);
  const [pinAllSessions, setPinAllSessions] = useState([]);
  const [pinPrograms, setPinPrograms] = useState([]);
  const [pinSelectedProgram, setPinSelectedProgram] = useState(null);
  const [pinDataLoading, setPinDataLoading] = useState(false);

  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingPain, setLoadingPain] = useState(true);
  const [loadingNotes, setLoadingNotes] = useState(true);

  const [errorUser, setErrorUser] = useState(null);
  const [errorSummary, setErrorSummary] = useState(null);
  const [errorSessions, setErrorSessions] = useState(null);
  const [errorPain, setErrorPain] = useState(null);
  const [errorNotes, setErrorNotes] = useState(null);
  
  const getDaysFromRange = (range) => {
    if (range === 'all') return 99999;
    return parseInt(range, 10);
  };

  const fetchSummary = useCallback(async (days) => {
    setLoadingSummary(true);
    setErrorSummary(null);
    try {
      const { data, error } = await supabase.rpc('get_user_workout_summary', { p_user_id: userId, p_days: days });
      if (error) throw error;
      if (data && data.length > 0) setSummary(data[0]);
      else setSummary(null);
    } catch (err) {
      console.error('Error fetching summary:', err);
      setErrorSummary('Failed to load stats.');
    } finally {
      setLoadingSummary(false);
    }
  }, [userId]);

  const fetchPainHistory = useCallback(async (days) => {
    setLoadingPain(true);
    setErrorPain(null);
    try {
      const { data, error } = await supabase.rpc('get_user_pain_history', { p_user_id: userId, p_days: days });
      if (error) throw error;
      setPainHistory(data || []);
    } catch (err) {
      console.error('Error fetching pain history:', err);
      setErrorPain('Failed to load pain log.');
    } finally {
      setLoadingPain(false);
    }
  }, [userId]);

  const fetchNotes = useCallback(async () => {
    if (!userId || typeof userId !== 'string') return;
    setLoadingNotes(true);
    setErrorNotes(null);
    try {
      const { data, error } = await supabase.rpc('admin_get_recent_notes', { p_limit: 20, p_exercise_id: null, p_user_id: userId });
      if (error) throw error;
      setNotesHistory(data || []);
    } catch (err) {
      console.error('Error fetching notes:', err);
      setErrorNotes('Failed to load notes.');
    } finally {
      setLoadingNotes(false);
    }
  }, [userId]);

  const handleReplyToNote = useCallback(async (exerciseCompletionId, replyText) => {
    const { error } = await supabase.rpc('admin_reply_to_note', {
      p_exercise_completion_id: exerciseCompletionId,
      p_reply_text: replyText,
    });
    if (error) throw error;
    await fetchNotes();
  }, [fetchNotes]);

  useEffect(() => {
    let cancelled = false;
    const fetchInitialData = async () => {
      setLoadingUser(true);
      setErrorUser(null);
      try {
        const { data: profileData, error: profileError } = await supabase
          .rpc('staff_get_user_profiles', { p_user_ids: [userId] })
          .single();

        if (profileError) throw profileError;
        
        if (profileData) {
          setUser({
            user_id: profileData.id,
            full_name: profileData.full_name,
            email: profileData.email,
            role: profileData.role,
            last_sign_in_at: profileData.last_sign_in_at,
            gender: profileData.gender,
            date_of_birth: profileData.date_of_birth,
            height_cm: profileData.height_cm,
            weight_kg: profileData.weight_kg,
            onboarding_step: profileData.onboarding_step,
            user_journey_type: profileData.user_journey_type,
            language: profileData.language,
            created_at: profileData.created_at,
            review_requested_at: profileData.review_requested_at,
            last_platform: profileData.last_platform,
            assigned_coach_id: profileData.assigned_coach_id,
            last_device_model: profileData.last_device_model,
          });

          if (canSeeBilling) {
            const { data: activationData, error: activationError } = await supabase
              .from('activation_dashboard')
              .select('user_email')
              .eq('user_email', profileData.email)
              .limit(1);

            if (!activationError && activationData && activationData.length > 0) {
              setIsFavvityUser(true);
            }
          }

          // Fetch onboarding data + Garmin connection in parallel
          const [prefRes, discRes, focusRes, programsRes, garminRes] = await Promise.all([
            supabase.from('user_preferences').select('experience_level').eq('user_id', userId).single(),
            supabase.from('user_disciplines').select('discipline_id, disciplines(name)').eq('user_id', userId),
            supabase.from('user_training_focuses').select('training_focus_id, training_focuses(name)').eq('user_id', userId),
            canSeeBilling
              ? supabase.from('user_programs').select('program_id, is_active, access_type, programs(name)').eq('user_id', userId)
              : supabase.rpc('staff_get_user_programs', { p_user_id: userId }),
            supabase.from('garmin_connections').select('status, connected_at, last_synced_at, last_sync_status').eq('user_id', userId).maybeSingle(),
          ]);

          if (garminRes.data) setGarminConnection(garminRes.data);

          setOnboardingData({
            experience_level: prefRes.data?.experience_level || null,
            disciplines: (discRes.data || []).map(d => d.disciplines?.name).filter(Boolean),
            training_focuses: (focusRes.data || []).map(f => f.training_focuses?.name).filter(Boolean),
            programs: (programsRes.data || []).map(p => ({
              name: p.programs?.name ?? p.program_name,
              is_active: p.is_active,
              access_type: p.access_type,
            })),
          });
          setLoadingOnboarding(false);

          // Fetch PostHog activity (non-blocking — fires and resolves independently)
          if (profileData.email) {
            supabase.functions.invoke('posthog-user-activity', {
              body: { email: profileData.email, user_id: profileData.id },
            }).then(({ data, error: fnError }) => {
              if (cancelled) return;
              if (!fnError && data && data.last_active) {
                setPosthogActivity(data);
              }
              setLoadingPosthogActivity(false);
            }).catch(() => {
              if (!cancelled) setLoadingPosthogActivity(false);
            });
          } else {
            setLoadingPosthogActivity(false);
          }
        } else {
          setErrorUser('User not found.');
          setLoadingOnboarding(false);
          setLoadingPosthogActivity(false);
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        if (err.code === 'PGRST116') {
          setErrorUser('User not found.');
        } else {
          setErrorUser('Failed to fetch user data.');
        }
        setLoadingOnboarding(false);
        setLoadingPosthogActivity(false);
      } finally {
        setLoadingUser(false);
      }

      setLoadingSessions(true);
      setErrorSessions(null);
      try {
        const { data, error } = await supabase.from('user_training_sessions').select(`*, sessions ( name )`).eq('user_id', userId).order('started_at', { ascending: false }).limit(10);
        if (error) throw error;
        setRecentSessions(data || []);
      } catch (err) {
        console.error('Error fetching recent sessions:', err);
        setErrorSessions('Failed to load recent activity.');
      } finally {
        setLoadingSessions(false);
      }

      if (userId && !cancelled) await fetchNotes();
    };

    if (userId) fetchInitialData();
    return () => { cancelled = true; };
  }, [userId, fetchNotes, canSeeBilling]);
  
  useEffect(() => {
    if (userId) {
      const days = getDaysFromRange(dateRange);
      fetchSummary(days);
      fetchPainHistory(days);
    }
  }, [userId, dateRange, fetchSummary, fetchPainHistory]);

  const fetchAssignedSessions = useCallback(async () => {
    if (!userId) return;
    setLoadingAssignedSessions(true);
    try {
      const { data: assigned } = await supabase
        .from('client_assigned_sessions')
        .select('id, session_id, assigned_at, due_date, notes, is_active, sessions(name, duration_minutes, program_id, programs(name)), assigned_by_profile:assigned_by(full_name)')
        .eq('client_id', userId)
        .order('assigned_at', { ascending: false });

      const assignedIds = (assigned || []).map(a => a.session_id);
      let completionMap = {};
      if (assignedIds.length > 0) {
        const { data: completions } = await supabase
          .from('user_training_sessions')
          .select('session_id, status')
          .eq('user_id', userId)
          .in('session_id', assignedIds);
        for (const c of (completions || [])) {
          const existing = completionMap[c.session_id];
          if (!existing || c.status === 'completed' || (c.status === 'in_progress' && existing !== 'completed')) {
            completionMap[c.session_id] = c.status;
          }
        }
      }

      setAssignedSessions((assigned || []).map(a => ({
        ...a,
        workout_status: completionMap[a.session_id] || null,
      })));
    } catch (err) {
      console.error('Error fetching assigned sessions:', err);
    } finally {
      setLoadingAssignedSessions(false);
    }
  }, [userId]);

  const MAX_PINNED_SESSIONS = 3;

  const fetchPinnedSessions = useCallback(async () => {
    if (!userId) return;
    setLoadingPinnedSessions(true);
    try {
      const { data, error } = await supabase
        .from('user_pinned_sessions')
        .select('id, session_id, sort_order, created_at, sessions(id, name, duration_minutes, program_id, programs(name))')
        .eq('user_id', userId)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      setPinnedSessions(data || []);
    } catch (err) {
      console.error('Error fetching pinned sessions:', err);
    } finally {
      setLoadingPinnedSessions(false);
    }
  }, [userId]);

  const handlePinSession = async (sessionId, sessionName) => {
    setPinningSessionId(sessionId);
    try {
      const { data: existing } = await supabase
        .from('user_pinned_sessions')
        .select('sort_order')
        .eq('user_id', userId)
        .order('sort_order', { ascending: false })
        .limit(1);

      const nextOrder = existing?.length ? existing[0].sort_order + 1 : 0;

      const { error } = await supabase
        .from('user_pinned_sessions')
        .insert({ user_id: userId, session_id: sessionId, sort_order: nextOrder });

      if (error) {
        if (error.message?.includes('PIN_LIMIT_REACHED')) {
          toast.error('This athlete already has 3 pinned sessions. Remove one first.');
          return;
        }
        throw error;
      }

      toast.success(`Pinned "${sessionName}" to athlete's dashboard`);
      setPinDialogOpen(false);
      setPinSearchTerm('');
      setPinSearchResults([]);
      await fetchPinnedSessions();
    } catch (err) {
      console.error('Error pinning session:', err);
      toast.error('Could not pin session', { description: err.message });
    } finally {
      setPinningSessionId(null);
    }
  };

  const handleUnpinSession = async (pin) => {
    const name = pin.sessions?.name || 'this session';
    if (!window.confirm(`Unpin "${name}" from this athlete's dashboard?`)) return;
    try {
      const { error } = await supabase
        .from('user_pinned_sessions')
        .delete()
        .eq('user_id', userId)
        .eq('session_id', pin.session_id);
      if (error) throw error;
      toast.success(`Unpinned "${name}"`);
      await fetchPinnedSessions();
    } catch (err) {
      console.error('Error unpinning session:', err);
      toast.error('Could not unpin session', { description: err.message });
    }
  };

  useEffect(() => {
    if (!canManageUser) return;
    const loadCoaches = async () => {
      const { data, error } = await supabase
        .from('coaches')
        .select('id, full_name, email, is_active')
        .order('full_name');
      if (error) {
        console.error('Error fetching coaches:', error);
        return;
      }
      setCoaches(data || []);
    };
    loadCoaches();
  }, [canManageUser]);

  // Fetch all active-program sessions once when pin dialog opens
  useEffect(() => {
    if (!pinDialogOpen) return;
    let cancelled = false;
    const load = async () => {
      setPinDataLoading(true);
      try {
        const { data, error } = await supabase
          .from('sessions')
          .select('id, name, duration_minutes, session_number, week_number, day_of_week, program_id, parent_session_id, programs!inner(id, name, is_active)')
          .eq('programs.is_active', true)
          .is('parent_session_id', null)
          .order('name');
        if (error) throw error;
        if (cancelled) return;
        const all = data || [];
        setPinAllSessions(all);
        const programMap = new Map();
        for (const s of all) {
          if (s.programs && !programMap.has(s.programs.id)) {
            programMap.set(s.programs.id, s.programs.name);
          }
        }
        const progs = Array.from(programMap.entries())
          .map(([id, name]) => ({ id, name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setPinPrograms(progs);
      } catch (err) {
        console.error('Error loading pin session data:', err);
      } finally {
        if (!cancelled) setPinDataLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [pinDialogOpen]);

  // Derive visible sessions for pin dialog from client-side filter
  const pinVisibleSessions = useMemo(() => {
    const pinnedIds = new Set(pinnedSessions.map(p => p.session_id));
    const term = pinSearchTerm.trim().toLowerCase();
    let filtered = pinAllSessions.filter(s => !pinnedIds.has(s.id));
    if (term) {
      filtered = filtered.filter(s => s.name.toLowerCase().includes(term));
    } else if (pinSelectedProgram) {
      filtered = filtered.filter(s => s.program_id === pinSelectedProgram);
    } else {
      return [];
    }
    return filtered.sort((a, b) =>
      (a.week_number ?? 0) - (b.week_number ?? 0) || (a.day_of_week ?? 0) - (b.day_of_week ?? 0) || a.name.localeCompare(b.name)
    );
  }, [pinAllSessions, pinnedSessions, pinSearchTerm, pinSelectedProgram]);

  const handleAssignedCoachChange = async (coachId) => {
    const nextId = coachId === 'none' ? null : coachId;
    setSavingAssignedCoach(true);
    try {
      const { error } = await supabase.rpc('admin_set_assigned_coach', {
        p_user_id: userId,
        p_coach_id: nextId,
      });
      if (error) throw error;
      setUser((prev) => ({ ...prev, assigned_coach_id: nextId }));
      toast.success(nextId ? 'Assigned coach updated' : 'Assigned coach cleared');
    } catch (err) {
      toast.error('Could not update assigned coach', { description: err.message });
    } finally {
      setSavingAssignedCoach(false);
    }
  };

  const handleUnassignSession = async (assignment) => {
    const name = assignment.sessions?.name || 'this session';
    if (!window.confirm(`Remove "${name}" from this athlete's schedule? The assignment is kept as history.`)) {
      return;
    }
    const { error } = await supabase
      .from('client_assigned_sessions')
      .update({ is_active: false })
      .eq('id', assignment.id);
    if (error) {
      toast.error('Could not remove assignment', { description: error.message });
      return;
    }
    toast.success(`Removed "${name}" from the schedule`);
    fetchAssignedSessions();
  };

  useEffect(() => {
    if (!userId) return;

    const fetchAlerts = async () => {
      setLoadingAlerts(true);
      try {
        const { data } = await supabase
          .from('client_alerts')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(20);
        setAlerts(data || []);
      } catch (err) {
        console.error('Error fetching alerts:', err);
      } finally {
        setLoadingAlerts(false);
      }
    };

    const fetchSubscription = async () => {
      setLoadingSubscription(true);
      try {
        const { data } = await supabase
          .from('subscriptions')
          .select('*, programs(name)')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(5);
        setSubscription(data?.[0] || null);
      } catch (err) {
        console.error('Error fetching subscription:', err);
      } finally {
        setLoadingSubscription(false);
      }
    };

    const fetchCoachEngagement = async () => {
      setLoadingCoachEngagement(true);
      try {
        const { data: sessions } = await supabase
          .from('agent_sessions')
          .select('id, agent_type, message_count, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });

        const sessionIds = (sessions || []).map((s) => s.id);
        let messagesBySession = {};
        if (sessionIds.length > 0) {
          const { data: msgs, error: msgErr } = await supabase
            .from('agent_messages')
            .select('id, session_id, role, content, created_at')
            .in('session_id', sessionIds)
            .order('created_at', { ascending: true });
          if (!msgErr && msgs) {
            for (const m of msgs) {
              if (!messagesBySession[m.session_id]) messagesBySession[m.session_id] = [];
              messagesBySession[m.session_id].push(m);
            }
          }
        }

        const threads = (sessions || []).map((s) => ({
          id: s.id,
          agent_type: s.agent_type,
          message_count: s.message_count,
          created_at: s.created_at,
          messages: messagesBySession[s.id] || [],
        }));
        setAiConversations(threads);

        const { data: feedback } = await supabase
          .from('agent_feedback')
          .select('rating')
          .eq('user_id', userId);

        const totalConversations = sessions?.length || 0;
        const totalMessages = sessions?.reduce((sum, s) => sum + (s.message_count || 0), 0) || 0;
        const lastChat = sessions?.[0]?.created_at || null;
        const avgRating = feedback?.length > 0
          ? (feedback.reduce((sum, f) => sum + f.rating, 0) / feedback.length)
          : null;

        setCoachEngagement({
          total_conversations: totalConversations,
          total_messages: totalMessages,
          last_chat: lastChat,
          avg_rating: avgRating,
          feedback_count: feedback?.length || 0,
        });
      } catch (err) {
        console.error('Error fetching coach engagement:', err);
        setAiConversations([]);
      } finally {
        setLoadingCoachEngagement(false);
      }
    };

    const fetch360Data = async () => {
      if (canSeeAlerts) await fetchAlerts();
      else setLoadingAlerts(false);

      if (canSeeBilling) await fetchSubscription();
      else setLoadingSubscription(false);

      if (canSeeAiChats) await fetchCoachEngagement();
      else setLoadingCoachEngagement(false);

      // Program Progress
      setLoadingProgramProgress(true);
      try {
        const { data: userPrograms } = await supabase
          .rpc('staff_get_user_programs', { p_user_id: userId, p_active_only: true });

        if (userPrograms && userPrograms.length > 0) {
          const { data: allUserSessions } = await supabase
            .from('user_training_sessions')
            .select('session_id, status, sessions(program_id, parent_session_id)')
            .eq('user_id', userId);

          const { data: programSessions } = await supabase
            .from('sessions')
            .select('id, program_id')
            .in('program_id', userPrograms.map(p => p.program_id))
            .is('parent_session_id', null);

          const progress = userPrograms.map(up => {
            const totalInProgram = programSessions?.filter(s => s.program_id === up.program_id).length || 0;
            const completedByUser = allUserSessions?.filter(
              us => us.sessions?.program_id === up.program_id && us.status === 'completed' && !us.sessions?.parent_session_id
            ).length || 0;

            return {
              program_name: up.program_name || 'Unknown',
              program_id: up.program_id,
              total_sessions: totalInProgram,
              completed_sessions: completedByUser,
              completion_pct: totalInProgram > 0 ? Math.round((completedByUser / totalInProgram) * 100) : 0,
            };
          });
          setProgramProgress(progress);
        }
      } catch (err) {
        console.error('Error fetching program progress:', err);
      } finally {
        setLoadingProgramProgress(false);
      }

      // Email History
      setLoadingEmails(true);
      try {
        const { data } = await supabase
          .from('email_logs')
          .select('id, subject, status, opened_at, clicked_at, bounced_at, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(20);
        setEmailHistory(data || []);
      } catch (err) {
        console.error('Error fetching email history:', err);
      } finally {
        setLoadingEmails(false);
      }

      // Assigned Sessions
      await fetchAssignedSessions();

      // Pinned Sessions
      await fetchPinnedSessions();
    };

    fetch360Data();
  }, [userId, canSeeAlerts, canSeeBilling, canSeeAiChats, fetchAssignedSessions, fetchPinnedSessions]);
  
  const handleResolveAlert = async (alertId) => {
    try {
      const { error } = await supabase
        .from('client_alerts')
        .update({ is_resolved: true, resolved_at: new Date().toISOString() })
        .eq('id', alertId);
      if (error) throw error;
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, is_resolved: true, resolved_at: new Date().toISOString() } : a));
      toast.success('Alert resolved');
    } catch (err) {
      console.error('Error resolving alert:', err);
      toast.error('Failed to resolve alert');
    }
  };

  const handleExportReport = async () => {
    setIsExporting(true);
    toast("Generating Report...", { description: "Please wait while we compile the user's activity report." });

    try {
      const days = 99999;
      const [summaryRes, painRes, notesRes, sessionsRes] = await Promise.all([
        supabase.rpc('get_user_workout_summary', { p_user_id: userId, p_days: days }),
        supabase.rpc('get_user_pain_history', { p_user_id: userId, p_days: days }),
        supabase.rpc('admin_get_recent_notes', { p_limit: 100, p_exercise_id: null, p_user_id: userId }),
        supabase.from('user_training_sessions').select(`*, sessions ( name )`).eq('user_id', userId).order('started_at', { ascending: false }).limit(100)
      ]);

      if (summaryRes.error || painRes.error || notesRes.error || sessionsRes.error) {
        throw new Error('Failed to fetch complete data for report.');
      }

      const reportSummary = summaryRes.data[0];
      const reportPain = painRes.data;
      const reportNotes = notesRes.data;
      const reportSessions = sessionsRes.data;

      let reportContent = `User Activity Report\n`;
      reportContent += `Generated on: ${new Date().toLocaleDateString()}\n`;
      reportContent += `==============================\n\n`;

      reportContent += `USER INFORMATION\n`;
      reportContent += `------------------------------\n`;
      reportContent += `Name: ${user.full_name}\n`;
      reportContent += `Email: ${user.email}\n`;
      reportContent += `Role: ${user.role}\n\n`;

      reportContent += `ALL-TIME ACTIVITY SUMMARY\n`;
      reportContent += `------------------------------\n`;
      if (reportSummary) {
        reportContent += `Total Workouts: ${reportSummary.total_workouts || 0}\n`;
        reportContent += `Total Exercises: ${reportSummary.total_exercises || 0}\n`;
        reportContent += `Average RPE: ${reportSummary.avg_rpe?.toFixed(1) || 'N/A'} / 10\n`;
        reportContent += `Average Pain Level: ${reportSummary.avg_pain_level?.toFixed(1) || 'N/A'} / 10\n`;
      } else {
        reportContent += `No summary data available.\n`;
      }
      reportContent += `\n`;

      reportContent += `RECENT SESSIONS\n`;
      reportContent += `------------------------------\n`;
      if (reportSessions && reportSessions.length > 0) {
        reportSessions.forEach(session => {
          reportContent += `* ${session.sessions?.name || 'Unnamed Session'} (${new Date(session.started_at).toLocaleString()})\n`;
          reportContent += `  Status: ${session.status}\n`;
          if(session.duration_seconds) reportContent += `  Duration: ${Math.round(session.duration_seconds / 60)} mins\n`;
          if(session.session_rpe) reportContent += `  RPE: ${session.session_rpe}/10\n`;
          if(session.session_notes) reportContent += `  Notes: ${session.session_notes}\n`;
          reportContent += `\n`;
        });
      } else {
        reportContent += `No recent sessions recorded.\n\n`;
      }

      reportContent += `PAIN LOG (ALL-TIME)\n`;
      reportContent += `------------------------------\n`;
      if (reportPain && reportPain.length > 0) {
        reportPain.forEach(log => {
          reportContent += `* ${log.exercise_name} (${new Date(log.completed_at).toLocaleString()})\n`;
          reportContent += `  Pain Level: ${log.pain_level}/10\n`;
          if(log.rpe) reportContent += `  RPE: ${log.rpe}/10\n`;
          if(log.notes) reportContent += `  Notes: "${log.notes}"\n`;
          reportContent += `\n`;
        });
      } else {
        reportContent += `No pain reported.\n\n`;
      }

      reportContent += `USER NOTES (ALL-TIME)\n`;
      reportContent += `------------------------------\n`;
      if (reportNotes && reportNotes.length > 0) {
        reportNotes.forEach(note => {
          reportContent += `* ${note.exercise_name} (${new Date(note.completed_at).toLocaleString()})\n`;
          reportContent += `  Note: "${note.notes}"\n`;
          if(note.rpe_reported != null) reportContent += `  RPE: ${note.rpe_reported}/10\n`;
          if(note.pain_level_reported != null) reportContent += `  Pain: ${note.pain_level_reported}/10\n`;
          reportContent += `\n`;
        });
      } else {
        reportContent += `No notes recorded.\n\n`;
      }

      const blob = new Blob([reportContent], { type: 'text/plain;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const dateString = new Date().toISOString().split('T')[0];
      link.download = `${user.full_name.replace(/\s/g, '_')}_activity_report_${dateString}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Report Downloaded!", { description: "The user activity report has been successfully generated." });
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error("Export Failed", { description: "Could not generate the report. Please try again." });
    } finally {
      setIsExporting(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const avatarColors = ['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'];
  const colorIndex = user ? (user.full_name || 'A').charCodeAt(0) % avatarColors.length : 0;
  
  const getStatusVariant = (status) => {
    switch(status) {
      case 'completed': return 'success';
      case 'abandoned': return 'warning';
      case 'in_progress': return 'default';
      default: return 'secondary';
    }
  };

  const getPainLevelVariant = (level) => {
    if (level > 6) return 'destructive';
    if (level >= 3) return 'warning';
    return 'success';
  };

  const dateRangeLabel = {
    '7': '7-Day',
    '30': '30-Day',
    '90': '90-Day',
    'all': 'All-Time'
  }[dateRange];

  const filteredRecentSessions = recentSessions.filter(
    session => session.status === 'completed' || session.status === 'in_progress'
  );

  const formatAgentLabel = (agentType) => {
    if (!agentType) return 'AI coach';
    return agentType.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const BLOCK_RE = /:::(exercise_cards|session_proposal|program_progress|answer_chips|knowledge_card|knowledge_summary)\n([\s\S]*?)\n:::/g;

  const renderAssistantContent = (text) => {
    if (!text) return <span className="text-muted-foreground italic">(empty)</span>;
    const segments = [];
    let lastIdx = 0;
    let match;
    const re = new RegExp(BLOCK_RE.source, 'g');
    while ((match = re.exec(text)) !== null) {
      if (match.index > lastIdx) {
        segments.push({ type: 'text', value: text.slice(lastIdx, match.index) });
      }
      try {
        const payload = JSON.parse(match[2].trim());
        segments.push({ type: match[1], data: payload });
      } catch {
        segments.push({ type: 'text', value: match[0] });
      }
      lastIdx = match.index + match[0].length;
    }
    if (lastIdx < text.length) {
      segments.push({ type: 'text', value: text.slice(lastIdx) });
    }
    if (segments.length === 0) {
      segments.push({ type: 'text', value: text });
    }

    return segments.map((seg, i) => {
      if (seg.type === 'text') {
        return <span key={i} className="whitespace-pre-wrap">{seg.value}</span>;
      }
      if (seg.type === 'session_proposal') {
        const exercises = Array.isArray(seg.data?.exercises) ? seg.data.exercises : [];
        const summary = seg.data?.summary;
        return (
          <div key={i} className="my-3 rounded-md border bg-muted/30 overflow-hidden">
            <div className="px-3 py-2 bg-muted/60 border-b">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Session Proposal: {seg.data?.title || 'Untitled'}
              </p>
              {summary && (
                <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
                  {summary.phase && <span>Phase: {summary.phase}</span>}
                  {summary.duration && <span>Duration: {summary.duration}</span>}
                  {summary.focus && <span>Focus: {summary.focus}</span>}
                </div>
              )}
            </div>
            <div className="divide-y">
              {exercises.map((ex, j) => (
                <div key={j} className="px-3 py-1.5 flex items-center gap-3 text-xs">
                  <Badge variant="outline" className="text-[10px] capitalize flex-shrink-0">{ex.group?.replace('_', ' ') || 'main'}</Badge>
                  <span className="font-medium flex-1 truncate">{ex.name}</span>
                  <span className="text-muted-foreground flex-shrink-0">
                    {ex.sets && `${ex.sets}x`}{ex.reps || ''}{ex.duration_seconds ? `${ex.duration_seconds}s` : ''}
                    {ex.rpe ? ` @RPE${ex.rpe}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      }
      if (seg.type === 'exercise_cards') {
        const exercises = Array.isArray(seg.data?.exercises) ? seg.data.exercises : [];
        return (
          <div key={i} className="my-3 rounded-md border bg-muted/30 overflow-hidden">
            <div className="px-3 py-2 bg-muted/60 border-b">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {seg.data?.title || 'Exercise Recommendations'}
              </p>
            </div>
            <div className="divide-y">
              {exercises.map((ex, j) => (
                <div key={j} className="px-3 py-1.5 text-xs flex items-center gap-2">
                  <span className="font-medium">{ex.name}</span>
                  {ex.primary_region && <Badge variant="secondary" className="text-[10px]">{ex.primary_region}</Badge>}
                  {ex.equipment && <span className="text-muted-foreground">{ex.equipment}</span>}
                </div>
              ))}
            </div>
          </div>
        );
      }
      if (seg.type === 'program_progress') {
        return (
          <div key={i} className="my-3 rounded-md border bg-muted/30 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
              Program Progress: {seg.data?.program_name || ''}
            </p>
            <div className="flex flex-wrap gap-3 text-xs">
              {seg.data?.sessions_completed != null && <span>{seg.data.sessions_completed}/{seg.data.total_sessions} sessions</span>}
              {seg.data?.current_phase && <span>Phase: {seg.data.current_phase}</span>}
              {seg.data?.avg_rpe && <span>Avg RPE: {seg.data.avg_rpe}</span>}
            </div>
          </div>
        );
      }
      // Fallback for other block types
      return (
        <div key={i} className="my-3 rounded-md border bg-muted/30 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{seg.type.replace('_', ' ')}</p>
          <pre className="text-xs text-muted-foreground overflow-x-auto">{JSON.stringify(seg.data, null, 2)}</pre>
        </div>
      );
    });
  };

  return (
    <>
      <Helmet>
        <title>{user ? `${user.full_name} - ` : ''}User Detail - My Movement Space</title>
      </Helmet>
      
      <div className="mb-6">
        <Button asChild variant="outline">
          <Link to="/user-activity">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to User Activity
          </Link>
        </Button>
      </div>

      {loadingUser && <ProfileSkeleton />}
      {errorUser && !loadingUser && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 gap-2">
            <p className="text-sm font-medium text-destructive">{errorUser}</p>
          </CardContent>
        </Card>
      )}
      
      {!loadingUser && !errorUser && user && (
        <>
          <header className="flex flex-col sm:flex-row items-start justify-between gap-6 mb-8">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className={`w-24 h-24 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-4xl ${avatarColors[colorIndex]}`}>
                {getInitials(user.full_name)}
              </div>
              <div className="text-center sm:text-left">
                <div className="flex items-center gap-3 justify-center sm:justify-start">
                  <h1 className="text-2xl font-semibold tracking-tight">{user.full_name}</h1>
                  <Badge variant={user.role === 'admin' ? 'destructive' : 'secondary'}>{user.role === 'admin' ? 'Coach' : user.role}</Badge>
                </div>
                <div className="flex items-center gap-1 mt-1 justify-center sm:justify-start">
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => {
                      navigator.clipboard.writeText(user.email);
                      toast.success('Email copied');
                    }}
                  >
                    <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                  <SendEmailButton userId={user.user_id} fullName={user.full_name} email={user.email} />
                </div>
                {canManageUser && (
                <div className="mt-3 flex flex-col items-center sm:items-start gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Assigned coach</span>
                    <Select
                      disabled={savingAssignedCoach}
                      onValueChange={handleAssignedCoachChange}
                      value={user.assigned_coach_id || 'none'}
                    >
                      <SelectTrigger className="h-8 w-[220px] text-xs">
                        <SelectValue placeholder="No assigned coach" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No assigned coach</SelectItem>
                        {coaches
                          .filter((coach) => coach.is_active !== false || coach.id === user.assigned_coach_id)
                          .map((coach) => (
                            <SelectItem key={coach.id} value={coach.id}>
                              {coach.full_name || coach.email || 'Unnamed coach'}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    The athlete app shows this coach as “Assigned by”.
                  </p>
                  <SupportAssignmentControl userId={userId} />
                </div>
                )}
                <div className="flex items-center justify-center sm:justify-start gap-3 mt-4 flex-wrap">
                  <Badge variant={getEngagementStatus(posthogActivity?.last_active ?? user.last_sign_in_at).variant}>
                    {getEngagementStatus(posthogActivity?.last_active ?? user.last_sign_in_at).label}
                  </Badge>
                  {isFavvityUser && (
                    <Badge variant="default">Favvity User</Badge>
                  )}
                  {garminConnection?.status === 'connected' ? (
                    <Badge variant="success">
                      <Watch className="mr-1 h-3 w-3" /> Garmin Connected
                    </Badge>
                  ) : garminConnection?.status === 'expired' || garminConnection?.status === 'error' ? (
                    <Badge variant="warning">
                      <Watch className="mr-1 h-3 w-3" /> Garmin {garminConnection.status}
                    </Badge>
                  ) : null}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    {loadingPosthogActivity ? (
                      <span className="text-xs">Loading activity...</span>
                    ) : (
                      <span>Last active: {(posthogActivity?.last_active ?? user.last_sign_in_at) ? formatRelativeTime(posthogActivity?.last_active ?? user.last_sign_in_at) : 'Never'}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={handleExportReport} disabled={isExporting} variant="outline">
                {isExporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                Export Report
              </Button>
              {canManageUser && (
                <Button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  variant="outline"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Account
                </Button>
              )}
            </div>
          </header>

          {/* Client Alerts Banner */}
          {!loadingAlerts && alerts.filter(a => !a.is_resolved).length > 0 && (
            <div className="mb-6 space-y-2">
              {alerts.filter(a => !a.is_resolved).map(alert => (
                <div
                  key={alert.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    alert.severity === 'high' ? 'border-destructive/50 bg-destructive/5' :
                    alert.severity === 'medium' ? 'border-yellow-500/50 bg-yellow-500/5' :
                    'border-border bg-muted/30'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <AlertTriangle className={`h-4 w-4 flex-shrink-0 ${
                      alert.severity === 'high' ? 'text-destructive' :
                      alert.severity === 'medium' ? 'text-yellow-600' :
                      'text-muted-foreground'
                    }`} />
                    <div>
                      <p className="text-sm font-medium">{alert.title}</p>
                      <p className="text-xs text-muted-foreground">{alert.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={alert.severity === 'high' ? 'destructive' : alert.severity === 'medium' ? 'warning' : 'secondary'}>
                      {alert.alert_type}
                    </Badge>
                    <Button size="sm" variant="ghost" onClick={() => handleResolveAlert(alert.id)}>
                      <CheckCheck className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <Separator className="my-8" />

          {/* Quick Stats Row: Subscription, Completion Rate, Coach Engagement, Wearable Data */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
            {/* Subscription Status */}
            {canSeeBilling && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Subscription</span>
                </div>
                {loadingSubscription ? (
                  <Skeleton className="h-5 w-24" />
                ) : subscription ? (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Badge variant={
                        subscription.status === 'active' ? 'success' :
                        subscription.status === 'trialing' ? 'default' :
                        subscription.status === 'past_due' ? 'warning' :
                        'destructive'
                      }>
                        {subscription.status}
                      </Badge>
                      {subscription.subscription_type && (
                        <span className="text-xs text-muted-foreground capitalize">{subscription.subscription_type}</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {subscription.purchase_platform && `Platform: ${subscription.purchase_platform}`}
                      {subscription.current_period_end && ` · Renews: ${new Date(subscription.current_period_end).toLocaleDateString()}`}
                    </p>
                    {subscription.cancel_at_period_end && (
                      <p className="text-xs text-destructive font-medium">Cancels at period end</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No subscription</p>
                )}
              </CardContent>
            </Card>
            )}

            {/* Program Completion Rate */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <PercentCircle className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Completion Rate</span>
                </div>
                {loadingProgramProgress ? (
                  <Skeleton className="h-5 w-24" />
                ) : programProgress.length > 0 ? (
                  <div className="space-y-2">
                    {programProgress.map(p => (
                      <div key={p.program_id}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="font-medium truncate mr-2">{p.program_name}</span>
                          <span className="text-muted-foreground flex-shrink-0">{p.completed_sessions}/{p.total_sessions}</span>
                        </div>
                        <Progress value={p.completion_pct} className="h-1.5" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No active programs</p>
                )}
              </CardContent>
            </Card>

            {/* AI Coach Engagement */}
            {canSeeAiChats && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Bot className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">AI Coach</span>
                </div>
                {loadingCoachEngagement ? (
                  <Skeleton className="h-5 w-24" />
                ) : coachEngagement && coachEngagement.total_conversations > 0 ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold">{coachEngagement.total_conversations}</span>
                      <span className="text-xs text-muted-foreground">conversations</span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                      <span>{coachEngagement.total_messages} messages</span>
                      {coachEngagement.avg_rating && (
                        <span>Avg rating: {coachEngagement.avg_rating.toFixed(1)}/5</span>
                      )}
                    </div>
                    {coachEngagement.last_chat && (
                      <p className="text-xs text-muted-foreground">Last chat: {formatRelativeTime(coachEngagement.last_chat)}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No AI interactions yet</p>
                )}
              </CardContent>
            </Card>
            )}

            {/* Wearable Data */}
            <Card
              className="cursor-pointer transition-colors hover:bg-muted/50"
              onClick={() => navigate(`/user-activity/${userId}/wearable`)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Watch className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Wearable Data</span>
                </div>
                {garminConnection?.status === 'connected' ? (
                  <div className="space-y-1">
                    <Badge variant="success">Garmin Connected</Badge>
                    {garminConnection.last_synced_at && (
                      <p className="text-xs text-muted-foreground">Last sync: {formatRelativeTime(garminConnection.last_synced_at)}</p>
                    )}
                  </div>
                ) : garminConnection?.status === 'expired' || garminConnection?.status === 'error' ? (
                  <div className="space-y-1">
                    <Badge variant="warning">Garmin {garminConnection.status}</Badge>
                    {garminConnection.last_synced_at && (
                      <p className="text-xs text-muted-foreground">Last sync: {formatRelativeTime(garminConnection.last_synced_at)}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No wearable connected</p>
                )}
              </CardContent>
            </Card>
          </div>

          <Separator className="my-8" />

          {/* Onboarding Profile Section */}
          <div className="mb-8">
            <h2 className="text-sm font-medium text-muted-foreground mb-4">Onboarding Profile</h2>
            {loadingOnboarding ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-4 space-y-2">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-5 w-32" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Onboarding Progress */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Onboarding Progress</span>
                      </div>
                      <Badge variant={user.onboarding_step >= 5 ? 'success' : 'secondary'}>
                        {user.onboarding_step >= 5 ? 'Complete' : `Step ${user.onboarding_step || 0} of 5`}
                      </Badge>
                    </div>
                    <Progress value={Math.min(((user.onboarding_step || 0) / 5) * 100, 100)} className="h-2" />
                    <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                      <span>Profile</span>
                      <span>Preferences</span>
                      <span>Disciplines</span>
                      <span>Focuses</span>
                      <span>Programs</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Profile Info Grid */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {/* Journey Type */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Compass className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Journey Type</span>
                      </div>
                      <p className="text-sm font-semibold capitalize">
                        {user.user_journey_type || 'Not set'}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Experience Level */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Zap className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Experience Level</span>
                      </div>
                      <p className="text-sm font-semibold capitalize">
                        {onboardingData?.experience_level || 'Not set'}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Language */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <BookOpen className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Language</span>
                      </div>
                      <p className="text-sm font-semibold">
                        {user.language === 'de' ? 'German' : user.language === 'en' ? 'English' : user.language || 'Not set'}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Biometrics */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Ruler className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Biometrics</span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {user.gender && <Badge variant="outline" className="capitalize">{user.gender}</Badge>}
                        {user.date_of_birth && (
                          <Badge variant="outline">
                            {Math.floor((Date.now() - new Date(user.date_of_birth)) / (1000 * 60 * 60 * 24 * 365.25))} yrs
                          </Badge>
                        )}
                        {user.height_cm && <Badge variant="outline">{user.height_cm} cm</Badge>}
                        {user.weight_kg && <Badge variant="outline">{user.weight_kg} kg</Badge>}
                        {!user.gender && !user.date_of_birth && !user.height_cm && !user.weight_kg && (
                          <span className="text-sm text-muted-foreground">Not provided</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Disciplines */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Flame className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Disciplines</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {onboardingData?.disciplines?.length > 0 ? (
                          onboardingData.disciplines.map(d => (
                            <Badge key={d} variant="secondary" className="text-xs">{d}</Badge>
                          ))
                        ) : (
                          <span className="text-sm text-muted-foreground">None selected</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Training Focuses */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Target className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Training Focuses</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {onboardingData?.training_focuses?.length > 0 ? (
                          onboardingData.training_focuses.map(f => (
                            <Badge key={f} variant="secondary" className="text-xs">{f}</Badge>
                          ))
                        ) : (
                          <span className="text-sm text-muted-foreground">None selected</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Active Programs */}
                {onboardingData?.programs?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Dumbbell className="h-4 w-4" />
                        Assigned Programs
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="grid gap-2 sm:grid-cols-2">
                        {onboardingData.programs.map((p, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2.5 rounded-md border bg-muted/30">
                            <div className="flex items-center gap-2 min-w-0">
                              {p.is_active ? (
                                <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                              ) : (
                                <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              )}
                              <span className="text-sm font-medium truncate">{p.name}</span>
                            </div>
                            <Badge variant={p.is_active ? 'success' : 'secondary'} className="text-xs flex-shrink-0 ml-2">
                              {(canSeeBilling && p.access_type) || (p.is_active ? 'active' : 'inactive')}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <AthleteTrainingCalendar
                  assignedSessions={assignedSessions}
                  clientEmail={user.email}
                  onAssigned={fetchAssignedSessions}
                  onUnassigned={fetchAssignedSessions}
                  userId={userId}
                />

                {/* Pinned Sessions */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Pin className="h-4 w-4" />
                        Pinned Sessions
                        <Badge variant={pinnedSessions.length >= MAX_PINNED_SESSIONS ? 'destructive' : 'secondary'} className="text-xs ml-1">
                          {pinnedSessions.length}/{MAX_PINNED_SESSIONS}
                        </Badge>
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1"
                        disabled={pinnedSessions.length >= MAX_PINNED_SESSIONS}
                        title={pinnedSessions.length >= MAX_PINNED_SESSIONS ? 'Remove a pin first' : 'Pin a session'}
                        onClick={() => { setPinDialogOpen(true); setPinSearchTerm(''); setPinSelectedProgram(null); }}
                      >
                        <Plus className="h-3 w-3" />
                        Add Pin
                      </Button>
                    </CardTitle>
                    <CardDescription>
                      Sessions pinned to the athlete's dashboard for quick access. The athlete can also manage their own pins.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {loadingPinnedSessions ? (
                      <div className="space-y-2">
                        {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-md" />)}
                      </div>
                    ) : pinnedSessions.length > 0 ? (
                      <div className="space-y-2">
                        {pinnedSessions.map((pin) => (
                          <div key={pin.id} className="rounded-md border bg-muted/30 flex items-center justify-between p-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <Pin className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                <span className="text-sm font-medium truncate">{pin.sessions?.name || 'Unknown Session'}</span>
                              </div>
                              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-muted-foreground ml-5.5">
                                {pin.sessions?.programs?.name && <span>{pin.sessions.programs.name}</span>}
                                {pin.sessions?.duration_minutes && <span>{pin.sessions.duration_minutes} min</span>}
                                <span>Pinned {new Date(pin.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-destructive flex-shrink-0 ml-3"
                              aria-label={`Unpin ${pin.sessions?.name || 'session'}`}
                              onClick={() => handleUnpinSession(pin)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <p className="text-xs text-muted-foreground mt-2">Changes appear on the athlete's dashboard within 2 minutes.</p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground py-2">No pinned sessions. Pin up to 3 sessions to the athlete's dashboard.</p>
                    )}
                  </CardContent>
                </Card>

                {/* Pin Session Dialog */}
                <Dialog open={pinDialogOpen} onOpenChange={(open) => { setPinDialogOpen(open); if (!open) { setPinSearchTerm(''); setPinSelectedProgram(null); } }}>
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Pin a Session</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      {/* Search bar */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search sessions across all programs…"
                          value={pinSearchTerm}
                          onChange={(e) => setPinSearchTerm(e.target.value)}
                          className="pl-9"
                          autoFocus
                        />
                      </div>

                      {pinDataLoading ? (
                        <div className="space-y-2 p-2">
                          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-md" />)}
                        </div>
                      ) : (
                        <>
                          {/* Program list — hidden when search text is active */}
                          {!pinSearchTerm.trim() && !pinSelectedProgram && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-2">Browse by program:</p>
                              <div className="max-h-72 overflow-y-auto space-y-1">
                                {pinPrograms.map((p) => (
                                  <button
                                    key={p.id}
                                    className="w-full flex items-center justify-between rounded-md p-2.5 text-left hover:bg-muted/50 transition-colors"
                                    onClick={() => setPinSelectedProgram(p.id)}
                                  >
                                    <span className="text-sm font-medium truncate">{p.name}</span>
                                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Back to programs + Session list */}
                          {!pinSearchTerm.trim() && pinSelectedProgram && (
                            <button
                              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-1"
                              onClick={() => setPinSelectedProgram(null)}
                            >
                              <ArrowLeft className="h-3 w-3" />
                              Back to programs
                            </button>
                          )}

                          <div className="max-h-72 overflow-y-auto space-y-1">
                            {pinVisibleSessions.length > 0 ? (
                              pinVisibleSessions.map((session) => (
                                <button
                                  key={session.id}
                                  className="w-full flex items-center justify-between rounded-md p-2.5 text-left hover:bg-muted/50 transition-colors disabled:opacity-50"
                                  disabled={pinningSessionId === session.id}
                                  onClick={() => handlePinSession(session.id, session.name)}
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                      {session.session_number != null && (
                                        <span className="text-xs font-mono text-muted-foreground w-6 text-right flex-shrink-0">S{String(session.session_number).padStart(2, '0')}</span>
                                      )}
                                      <span className="text-sm font-medium truncate">{session.name}</span>
                                    </div>
                                    <span className="text-xs text-muted-foreground ml-8">
                                      {pinSearchTerm.trim() && session.programs?.name ? `${session.programs.name} · ` : ''}
                                      {session.week_number != null ? `Wk ${session.week_number}` : ''}
                                      {session.duration_minutes ? ` · ${session.duration_minutes} min` : ''}
                                    </span>
                                  </div>
                                  {pinningSessionId === session.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground flex-shrink-0 ml-2" />
                                  ) : (
                                    <Pin className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
                                  )}
                                </button>
                              ))
                            ) : pinSearchTerm.trim() ? (
                              <p className="text-sm text-muted-foreground text-center py-6">No sessions found.</p>
                            ) : pinSelectedProgram ? (
                              <p className="text-sm text-muted-foreground text-center py-6">All sessions in this program are already pinned.</p>
                            ) : (
                              <p className="text-sm text-muted-foreground text-center py-6">Select a program or search by name.</p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>

                <BookmarkedExercisesCard userId={userId} />

                {/* Assigned Sessions */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <ClipboardList className="h-4 w-4" />
                      Assigned Sessions
                    </CardTitle>
                    <CardDescription>
                      Sessions a coach assigned to this athlete. Removing one hides it from the athlete app but keeps the row for history.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {loadingAssignedSessions ? (
                      <div className="space-y-2">
                        {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-md" />)}
                      </div>
                    ) : assignedSessions.length > 0 ? (
                      <div className="space-y-2">
                        {assignedSessions.map((a) => {
                          const statusLabel = a.workout_status === 'completed' ? 'Completed'
                            : a.workout_status === 'in_progress' ? 'In Progress'
                            : a.workout_status === 'abandoned' ? 'Abandoned'
                            : 'Not Started';
                          const statusVariant = a.workout_status === 'completed' ? 'success'
                            : a.workout_status === 'in_progress' ? 'default'
                            : a.workout_status === 'abandoned' ? 'warning'
                            : 'secondary';
                          const isExpanded = expandedAssignedSession === a.id;
                          const exercises = assignedSessionExercises[a.session_id];
                          return (
                            <div key={a.id} className="rounded-md border bg-muted/30 overflow-hidden">
                              <div
                                className="w-full flex items-center justify-between p-3 text-left hover:bg-muted/50 transition-colors cursor-pointer"
                                onClick={async () => {
                                  if (isExpanded) {
                                    setExpandedAssignedSession(null);
                                    return;
                                  }
                                  setExpandedAssignedSession(a.id);
                                  if (!exercises) {
                                    setLoadingAssignedExercises(prev => ({ ...prev, [a.session_id]: true }));
                                    try {
                                      const { data } = await supabase
                                        .from('session_exercises')
                                        .select('order_in_session, exercise_group, sets, reps, reps_per_side, duration_seconds, rest_between_sets_seconds, target_rpe, exercise_mode, load_type, coaching_notes, exercises(name)')
                                        .eq('session_id', a.session_id)
                                        .order('order_in_session', { ascending: true });
                                      setAssignedSessionExercises(prev => ({ ...prev, [a.session_id]: data || [] }));
                                    } catch (err) {
                                      console.error('Error fetching session exercises:', err);
                                      setAssignedSessionExercises(prev => ({ ...prev, [a.session_id]: [] }));
                                    } finally {
                                      setLoadingAssignedExercises(prev => ({ ...prev, [a.session_id]: false }));
                                    }
                                  }
                                }}
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium truncate">{a.sessions?.name || 'Unnamed Session'}</span>
                                    {!a.is_active && <Badge variant="outline" className="text-xs">Inactive</Badge>}
                                  </div>
                                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-muted-foreground">
                                    {a.sessions?.programs?.name && <span>{a.sessions.programs.name}</span>}
                                    {a.sessions?.duration_minutes && <span>{a.sessions.duration_minutes} min</span>}
                                    <span>Assigned {new Date(a.assigned_at).toLocaleDateString()}</span>
                                    {a.assigned_by_profile?.full_name && <span>by {a.assigned_by_profile.full_name}</span>}
                                    {a.due_date && <span>Due {new Date(a.due_date).toLocaleDateString()}</span>}
                                  </div>
                                  {a.notes && <p className="text-xs text-muted-foreground mt-1 italic">"{a.notes}"</p>}
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                  <Badge variant={statusVariant} className="text-xs">
                                    {statusLabel}
                                  </Badge>
                                  {a.is_active !== false && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                      aria-label={`Remove ${a.sessions?.name || 'session'} from this athlete's schedule`}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleUnassignSession(a);
                                      }}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </div>
                              </div>
                              {isExpanded && (
                                <div className="border-t">
                                  {loadingAssignedExercises[a.session_id] ? (
                                    <div className="p-4 space-y-2">
                                      {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-8 w-full rounded" />)}
                                    </div>
                                  ) : exercises && exercises.length > 0 ? (
                                    <div className="divide-y">
                                      {(() => {
                                        const groupLabels = { warm_up: 'Warm-Up', core: 'Core', activation: 'Activation', main_workout: 'Main Workout', auxiliary: 'Auxiliary', cool_down: 'Cool-Down' };
                                        let lastGroup = null;
                                        return exercises.map((ex, i) => {
                                          const showGroupHeader = ex.exercise_group && ex.exercise_group !== lastGroup;
                                          lastGroup = ex.exercise_group;
                                          const prescription = ex.reps_per_side
                                            ? `${ex.sets} × ${ex.reps_per_side}/side`
                                            : ex.reps
                                            ? `${ex.sets} × ${ex.reps}`
                                            : ex.duration_seconds
                                            ? `${ex.sets} × ${ex.duration_seconds}s`
                                            : `${ex.sets} sets`;
                                          return (
                                            <React.Fragment key={i}>
                                              {showGroupHeader && (
                                                <div className="px-4 py-1.5 bg-muted/60">
                                                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                    {groupLabels[ex.exercise_group] || ex.exercise_group}
                                                  </span>
                                                </div>
                                              )}
                                              <div className="px-4 py-2 flex items-center gap-3 text-sm">
                                                <span className="text-xs text-muted-foreground w-5 text-right flex-shrink-0">{ex.order_in_session}</span>
                                                <span className="font-medium flex-1 truncate">{ex.exercises?.name || 'Unknown'}</span>
                                                <span className="text-xs text-muted-foreground flex-shrink-0">{prescription}</span>
                                                {ex.target_rpe && (
                                                  <span className="text-xs text-muted-foreground flex-shrink-0">RPE {ex.target_rpe}</span>
                                                )}
                                                {ex.load_type && ex.load_type !== 'bodyweight' && (
                                                  <Badge variant="outline" className="text-[10px] capitalize flex-shrink-0">{ex.load_type}</Badge>
                                                )}
                                              </div>
                                            </React.Fragment>
                                          );
                                        });
                                      })()}
                                    </div>
                                  ) : (
                                    <p className="p-4 text-sm text-muted-foreground">No exercises found for this session.</p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground py-2">No sessions assigned yet.</p>
                    )}
                  </CardContent>
                </Card>

                {/* Account & Review Request */}
                <div className="grid gap-4 md:grid-cols-2">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Account</span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span>Joined: {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</span>
                        <span>Last active: {(posthogActivity?.last_active ?? user.last_sign_in_at) ? formatRelativeTime(posthogActivity?.last_active ?? user.last_sign_in_at) : 'Never'}</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Star className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">App Review Request</span>
                      </div>
                      {(() => {
                        const platform = user.last_platform || subscription?.purchase_platform || null;
                        const platformLabel = platform === 'ios' ? 'iOS (App Store)'
                          : platform === 'android' ? 'Android (Play Store)'
                          : platform === 'web' || platform === 'stripe' ? 'Web'
                          : null;
                        const reviewLink = platform === 'android'
                          ? 'https://play.google.com/store/apps/details?id=space.mymovement.app'
                          : 'https://apps.apple.com/app/id6758768415?action=write-review';
                        const firstName = user.full_name?.split(' ')[0] || user.full_name;
                        const whatsAppMsg = user.language === 'en'
                          ? `Hey ${firstName}! 👋 Quick question — how's your experience been with MyMovement so far? If you're enjoying it, it would mean a lot if you could leave a quick review (takes 30 seconds):\n${reviewLink}\nThanks so much! 🙏`
                          : `Hey ${firstName}! 👋 Kurze Frage — wie gefällt dir MyMovement bisher? Wenn du zufrieden bist, würde es mir mega viel bedeuten, wenn du eine kurze Bewertung hinterlässt (dauert 30 Sekunden):\n${reviewLink}\nDanke dir! 🙏`;

                        const handleMarkRequested = async () => {
                          try {
                            const { data: requestedAt, error } = await supabase.rpc(
                              'staff_mark_review_requested',
                              { p_user_id: user.user_id }
                            );
                            if (error) throw error;
                            setUser(prev => ({ ...prev, review_requested_at: requestedAt }));
                            toast.success('Marked as requested');
                          } catch (err) {
                            console.error('Error marking review requested:', err);
                            toast.error('Failed to update');
                          }
                        };

                        return (
                          <div className="space-y-3">
                            {(platform || user.last_device_model) && (
                              <div className="flex flex-wrap items-center gap-2">
                                {platformLabel && (
                                  <Badge variant={platform === 'ios' ? 'default' : platform === 'android' ? 'secondary' : 'outline'} className="text-xs">
                                    {platformLabel}
                                  </Badge>
                                )}
                                {user.last_device_model && (
                                  <span className="text-xs text-muted-foreground">{user.last_device_model}</span>
                                )}
                              </div>
                            )}
                            {!platform && !user.last_device_model && (
                              <p className="text-xs text-muted-foreground">Platform unknown</p>
                            )}
                            {user.review_requested_at ? (
                              <div className="space-y-1">
                                <Badge variant="success" className="text-xs">Requested</Badge>
                                <p className="text-xs text-muted-foreground">
                                  Sent {new Date(user.review_requested_at).toLocaleDateString()} ({formatRelativeTime(user.review_requested_at)})
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <Badge variant="warning" className="text-xs">Not Yet Asked</Badge>
                                {(platform === 'web' || platform === 'stripe') ? (
                                  <p className="text-xs text-muted-foreground">Web user — no store review applicable</p>
                                ) : (
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs"
                                      onClick={() => {
                                        navigator.clipboard.writeText(whatsAppMsg);
                                        toast.success('WhatsApp message copied');
                                      }}
                                    >
                                      <Copy className="h-3 w-3 mr-1.5" />
                                      Copy WhatsApp Message
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="default"
                                      className="h-7 text-xs"
                                      onClick={handleMarkRequested}
                                    >
                                      <CheckCheck className="h-3 w-3 mr-1.5" />
                                      Mark as Requested
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Activity from PostHog */}
                {loadingPosthogActivity ? (
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Activity className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Recent Activity</span>
                      </div>
                      <div className="space-y-3">
                        {[...Array(5)].map((_, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <Skeleton className="h-3 w-3 rounded-full" />
                            <Skeleton className="h-4 flex-1" />
                            <Skeleton className="h-3 w-20" />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : posthogActivity?.recent_actions?.length > 0 ? (
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Activity className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Recent Activity</span>
                        <span className="text-[10px] text-muted-foreground/60 ml-auto">via PostHog</span>
                      </div>
                      <div className="space-y-2">
                        {posthogActivity.recent_actions.map((action, i) => (
                          <div key={i} className="flex items-start gap-2.5 text-sm">
                            <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="text-foreground">{action.label}</span>
                              {action.screen && (
                                <span className="text-muted-foreground/60 text-xs ml-1.5">({action.screen.replace(/\/\(tabs\)\//g, '').replace(/\[.*?\]/g, '...').replace(/^\//,'')})</span>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground/60 whitespace-nowrap shrink-0">{formatRelativeTime(action.timestamp)}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : null}
              </div>
            )}
          </div>

          <Separator className="my-8" />

          {/* Activity Summary */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-sm font-medium text-muted-foreground">{dateRangeLabel} Activity Summary</h2>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loadingSummary && <SummarySkeleton />}
            {errorSummary && !loadingSummary && (
              <Card>
                <CardContent className="flex items-center justify-center h-24 text-sm text-destructive">{errorSummary}</CardContent>
              </Card>
            )}
            {!loadingSummary && !errorSummary && (
              summary ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total Workouts</CardTitle>
                      <Activity className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{summary.total_workouts || 0}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total Exercises</CardTitle>
                      <Dumbbell className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{summary.total_exercises || 0}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Avg. RPE</CardTitle>
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {summary.avg_rpe?.toFixed(1) || 'N/A'}
                        {summary.avg_rpe && <span className="text-sm font-normal text-muted-foreground">/10</span>}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Avg. Pain Level</CardTitle>
                      <Heart className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {summary.avg_pain_level?.toFixed(1) || 'N/A'}
                        {summary.avg_pain_level && <span className="text-sm font-normal text-muted-foreground">/10</span>}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card>
                  <CardContent className="flex items-center justify-center h-24 text-sm text-muted-foreground">
                    No activity recorded for this period.
                  </CardContent>
                </Card>
              )
            )}
          </div>

          <Tabs defaultValue="activity">
            <TabsList>
              <TabsTrigger value="activity">Recent Activity</TabsTrigger>
              <TabsTrigger value="pain_log">Pain Log</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
              {canSeeAiChats && <TabsTrigger value="ai_chats">AI Chats</TabsTrigger>}
              <TabsTrigger value="communications">Communications</TabsTrigger>
              {canSeeAlerts && <TabsTrigger value="alerts">Alerts</TabsTrigger>}
            </TabsList>
            <TabsContent value="activity">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Training Sessions</CardTitle>
                  <CardDescription>Last 10 sessions recorded.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingSessions && <ListSkeleton />}
                  {errorSessions && !loadingSessions && (
                    <div className="flex items-center justify-center h-48 text-sm text-destructive">{errorSessions}</div>
                  )}
                  {!loadingSessions && !errorSessions && (
                    filteredRecentSessions.length > 0 ? (
                      filteredRecentSessions.map((session) => (
                        <Card key={session.id} className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <Link to={`/user-activity/${userId}/session/${session.id}`}>
                                <h3 className="font-semibold hover:text-primary cursor-pointer transition-colors">
                                  {session.sessions?.name || 'Unnamed Session'}
                                </h3>
                              </Link>
                              <p className="text-sm text-muted-foreground">{new Date(session.started_at).toLocaleString()}</p>
                            </div>
                            {session.status !== 'abandoned' && <Badge variant={getStatusVariant(session.status)}>{session.status}</Badge>}
                          </div>
                          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                            {session.duration_seconds && <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" /><span>{Math.round(session.duration_seconds / 60)} min duration</span></div>}
                            {session.session_rpe && <div className="flex items-center gap-2"><Target className="h-4 w-4 text-muted-foreground" /><span>RPE: {session.session_rpe}/10</span></div>}
                          </div>
                          {session.session_notes && (
                            <div className="mt-4 p-3 bg-muted rounded-lg">
                              <div className="flex items-start gap-2 text-sm">
                                <StickyNote className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                                <p className="italic text-muted-foreground">{session.session_notes}</p>
                              </div>
                            </div>
                          )}
                        </Card>
                      ))
                    ) : <div className="text-center text-muted-foreground py-10">No recent activity found.</div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="pain_log">
              <Card>
                <CardHeader>
                  <CardTitle>Pain Log ({dateRangeLabel})</CardTitle>
                  <CardDescription>Exercises where pain was reported.</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingPain && <ListSkeleton />}
                  {errorPain && !loadingPain && (
                    <div className="flex items-center justify-center h-48 text-sm text-destructive">{errorPain}</div>
                  )}
                  {!loadingPain && !errorPain && (
                    painHistory.length > 0 ? (
                      <div className="space-y-4">
                        {painHistory.map((log, index) => (
                          <Card key={index} className="p-4 flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <Badge variant={getPainLevelVariant(log.pain_level)}>Pain: {log.pain_level}/10</Badge>
                                <h3 className="font-semibold">{log.exercise_name}</h3>
                              </div>
                              <p className="text-sm text-muted-foreground">{new Date(log.completed_at).toLocaleString()}</p>
                            </div>
                            <div className="flex flex-col items-start sm:items-end gap-2 text-sm">
                              {log.rpe && <div className="flex items-center gap-2"><Target className="h-4 w-4 text-muted-foreground" /><span>RPE: {log.rpe}/10</span></div>}
                              {log.notes && <div className="flex items-center gap-2 text-left sm:text-right"><MessageCircle className="h-4 w-4 text-muted-foreground" /><span className="italic text-muted-foreground">"{log.notes}"</span></div>}
                            </div>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <Card>
                        <CardContent className="flex flex-col items-center justify-center h-48 text-center text-muted-foreground">
                          <p className="font-medium mb-1">No pain reported in this period</p>
                          <p className="text-sm">Great news! Everything seems to be going smoothly.</p>
                        </CardContent>
                      </Card>
                    )
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="notes">
              <Card>
                <CardHeader>
                  <CardTitle>User Notes</CardTitle>
                  <CardDescription>Last 20 notes from workout sessions.</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingNotes && <ListSkeleton />}
                  {errorNotes && !loadingNotes && (
                    <div className="flex items-center justify-center h-48 text-sm text-destructive">{errorNotes}</div>
                  )}
                  {!loadingNotes && !errorNotes && (
                    notesHistory.length > 0 ? (
                      <div className="space-y-4">
                        {notesHistory.map((note, idx) => note?.completion_id ? (
                          <NoteCard
                            key={note.completion_id ?? idx}
                            note={note}
                            onReply={handleReplyToNote}
                            compact
                          />
                        ) : null)}
                      </div>
                    ) : (
                      <Card>
                        <CardContent className="flex flex-col items-center justify-center h-48 text-center text-muted-foreground">
                          <p className="font-medium mb-1">No Notes Recorded</p>
                          <p className="text-sm">This user hasn't left any notes on their exercises yet.</p>
                        </CardContent>
                      </Card>
                    )
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="ai_chats">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bot className="h-5 w-5" />
                    AI conversations
                  </CardTitle>
                  <CardDescription>
                    Full message transcripts from in-app AI coach sessions (read-only).
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingCoachEngagement ? <ListSkeleton /> : aiConversations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-48 text-center text-muted-foreground">
                      <Bot className="h-8 w-8 mb-2" />
                      <p className="font-medium mb-1">No AI chats yet</p>
                      <p className="text-sm">This user has not started a conversation with the AI coach.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {aiConversations.map((conv) => (
                        <Card key={conv.id} className="overflow-hidden">
                          <CardHeader className="py-3 px-4 bg-muted/40 border-b">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary">{formatAgentLabel(conv.agent_type)}</Badge>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(conv.created_at).toLocaleString()}
                                </span>
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {conv.messages.length} message{conv.messages.length !== 1 ? 's' : ''}
                                {conv.message_count != null && conv.message_count !== conv.messages.length ? (
                                  <span className="ml-1">(stored count: {conv.message_count})</span>
                                ) : null}
                              </span>
                            </div>
                          </CardHeader>
                          <CardContent className="p-3">
                            <div className="max-h-[32rem] overflow-y-auto space-y-3 rounded-md border bg-background p-3">
                              {conv.messages.length === 0 ? (
                                <p className="text-sm text-muted-foreground text-center py-6">
                                  No message rows found for this session.
                                </p>
                              ) : (
                                conv.messages.map((msg) => {
                                  const text = typeof msg.content === 'string' ? msg.content : (msg.content != null ? String(msg.content) : '');
                                  if (msg.role === 'user') {
                                    return (
                                      <div key={msg.id} className="flex justify-end">
                                        <div className="max-w-[85%] rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground whitespace-pre-wrap break-words">
                                          {text || <span className="opacity-70">(empty)</span>}
                                        </div>
                                      </div>
                                    );
                                  }
                                  if (msg.role === 'assistant') {
                                    return (
                                      <div key={msg.id} className="flex justify-start">
                                        <div className="max-w-[85%] rounded-lg border bg-card px-3 py-2 text-sm break-words">
                                          <div className="mb-1 flex items-center gap-1 text-xs font-medium text-muted-foreground">
                                            <Bot className="h-3 w-3" />
                                            Assistant
                                          </div>
                                          {renderAssistantContent(text)}
                                        </div>
                                      </div>
                                    );
                                  }
                                  if (msg.role === 'system') {
                                    return (
                                      <div key={msg.id} className="text-center">
                                        <p className="text-xs text-muted-foreground italic whitespace-pre-wrap break-words px-4">
                                          System: {text || '(empty)'}
                                        </p>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={msg.id} className="flex justify-center">
                                      <div className="max-w-[90%] rounded-md bg-muted/60 px-3 py-2 text-xs font-mono whitespace-pre-wrap break-all">
                                        <div className="mb-1 flex items-center gap-1 font-sans text-muted-foreground">
                                          <Wrench className="h-3 w-3" />
                                          <span className="uppercase">{msg.role}</span>
                                        </div>
                                        {text || '(empty)'}
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="communications">
              <Card>
                <CardHeader>
                  <CardTitle>Email Communications</CardTitle>
                  <CardDescription>Emails sent to this user from campaigns.</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingEmails ? <ListSkeleton /> : (
                    emailHistory.length > 0 ? (
                      <div className="space-y-3">
                        {emailHistory.map((email) => (
                          <div key={email.id} className="flex items-center justify-between p-3 rounded-md border">
                            <div className="flex items-start gap-3 min-w-0">
                              {email.opened_at ? (
                                <MailOpen className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                              ) : email.bounced_at ? (
                                <XCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                              ) : (
                                <Mail className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                              )}
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{email.subject || 'No subject'}</p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(email.created_at).toLocaleDateString()} at {new Date(email.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                              {email.clicked_at && (
                                <Badge variant="outline" className="text-xs gap-1">
                                  <MousePointerClick className="h-3 w-3" />
                                  Clicked
                                </Badge>
                              )}
                              {email.opened_at && !email.clicked_at && (
                                <Badge variant="outline" className="text-xs gap-1">
                                  <MailOpen className="h-3 w-3" />
                                  Opened
                                </Badge>
                              )}
                              {email.bounced_at && (
                                <Badge variant="destructive" className="text-xs">Bounced</Badge>
                              )}
                              {!email.opened_at && !email.bounced_at && (
                                <Badge variant="secondary" className="text-xs">{email.status || 'Sent'}</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-48 text-center text-muted-foreground">
                        <Mail className="h-8 w-8 mb-2" />
                        <p className="font-medium mb-1">No Emails Sent</p>
                        <p className="text-sm">No campaign emails have been sent to this user yet.</p>
                      </div>
                    )
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="alerts">
              <Card>
                <CardHeader>
                  <CardTitle>Alert History</CardTitle>
                  <CardDescription>All coaching alerts generated for this user.</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingAlerts ? <ListSkeleton /> : (
                    alerts.length > 0 ? (
                      <div className="space-y-3">
                        {alerts.map((alert) => (
                          <div
                            key={alert.id}
                            className={`p-3 rounded-md border ${alert.is_resolved ? 'opacity-60' : ''}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <AlertTriangle className={`h-4 w-4 ${
                                  alert.severity === 'high' ? 'text-destructive' :
                                  alert.severity === 'medium' ? 'text-yellow-600' :
                                  'text-muted-foreground'
                                }`} />
                                <span className="text-sm font-medium">{alert.title}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant={
                                  alert.severity === 'high' ? 'destructive' :
                                  alert.severity === 'medium' ? 'warning' :
                                  'secondary'
                                }>
                                  {alert.severity}
                                </Badge>
                                {alert.is_resolved ? (
                                  <Badge variant="outline" className="text-xs gap-1">
                                    <CheckCheck className="h-3 w-3" />
                                    Resolved
                                  </Badge>
                                ) : (
                                  <Button size="sm" variant="outline" onClick={() => handleResolveAlert(alert.id)}>
                                    Resolve
                                  </Button>
                                )}
                              </div>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{alert.description}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(alert.created_at).toLocaleDateString()}
                              {alert.resolved_at && ` · Resolved ${new Date(alert.resolved_at).toLocaleDateString()}`}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-48 text-center text-muted-foreground">
                        <CheckCircle2 className="h-8 w-8 mb-2" />
                        <p className="font-medium mb-1">No Alerts</p>
                        <p className="text-sm">No coaching alerts have been generated for this user.</p>
                      </div>
                    )
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
      <DeleteUserDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        targetUserId={userId}
        targetEmail={user?.email}
        onComplete={() => {
          setIsDeleteDialogOpen(false);
          navigate('/user-program');
        }}
      />
    </>
  );
};

export default UserActivityDetail;
